import { hash } from "daedalus-core";
import { STORM_KIND, regionWord, weatherAt, type WeatherKind, type WeatherTable } from "./weather.ts";

/**
 * Wind (ADR-144's amendment of 2026-10-31): how hard the air moves in a region in a turn and which way. It is drawn from the
 * same world seed, region and turn as `weatherAt`, so every map server and page agrees and nothing about it is sent or saved.
 * All arithmetic on drawn numbers is integer.
 */

/**
 * How hard the wind blows: 0 calm, 1 breeze, 2 windy, 3 storm.
 */
export type WindStrength = 0 | 1 | 2 | 3;

/**
 * Which way the wind blows across the screen.
 */
export type WindHeading = "left" | "right";

/**
 * The wind of one region in one turn.
 */
export interface Wind {
  readonly strength: WindStrength;
  readonly heading: WindHeading;
}

/**
 * The names of the strengths, by strength.
 */
export const WIND_NAMES = ["calm", "breeze", "windy", "storm"] as const;

/**
 * The strength of a storm's wind.
 */
export const STORM_WIND: WindStrength = 3;

/**
 * The share of clear turns that are calm, and the share that are calm or a breeze (the rest are windy), in percent.
 */
export const CLEAR_CALM_PERCENT = 30;
export const CLEAR_BREEZE_PERCENT = 75;

/**
 * The share of raining turns that are a breeze, in percent (the rest are windy; a rain is never calm).
 */
export const RAIN_BREEZE_PERCENT = 40;

const WIND_DOMAIN = 0x77696e64;
const HEADING_DOMAIN = WIND_DOMAIN + 1;
const PERCENT = 100;
const DRAW_SHIFT = 8;
const HEADING_SHIFT = 16;

/**
 * The strength of a turn's wind from the kind of weather and a draw.
 *
 * @param kind The turn's weather.
 * @param percent A uniform draw from 0 to 99.
 * @returns A storm is 3; a rain is a breeze or windy; a clear turn is calm, a breeze or windy.
 */
export function windStrengthFor(kind: WeatherKind, percent: number): WindStrength {
  if (kind === STORM_KIND) return STORM_WIND;
  if (kind === "rain") return percent < RAIN_BREEZE_PERCENT ? 1 : 2;
  if (percent < CLEAR_CALM_PERCENT) return 0;
  return percent < CLEAR_BREEZE_PERCENT ? 1 : 2;
}

/**
 * The wind of a region in a turn.
 *
 * The heading is drawn once per front, so a front blows one way; the strength is drawn per turn and follows the turn's weather
 * (`windStrengthFor`). The same inputs give the same wind on every map server and page; no clock, random source or stored
 * state is read.
 *
 * @param weather The weather table.
 * @param worldSeed The world's seed, an unsigned 32-bit integer.
 * @param regionId The region id (`weatherRegionOf`).
 * @param turnIndex The turn from `turnOf`; below 0 or not finite reads as 0.
 * @returns The strength and the heading.
 * @throws RangeError when the world seed is not an unsigned 32-bit integer.
 * @example
 * windAt(DEFAULT_WEATHER, worldWeatherSeed("main"), "ashfield", turnOf(serverNow));
 */
export function windAt(weather: WeatherTable, worldSeed: number, regionId: string, turnIndex: number): Wind {
  const turn = Number.isFinite(turnIndex) && turnIndex > 0 ? Math.floor(turnIndex) : 0;
  const region = regionWord(regionId);
  const kind = weatherAt(weather, worldSeed, regionId, turn).kind;
  const percent = (hash(worldSeed, region, WIND_DOMAIN, turn) >>> DRAW_SHIFT) % PERCENT;
  const front = Math.floor(turn / weather.frontTurns);
  const heading: WindHeading = ((hash(worldSeed, region, HEADING_DOMAIN, front) >>> HEADING_SHIFT) & 1) === 1 ? "right" : "left";
  return { strength: windStrengthFor(kind, percent), heading };
}
